import React, { useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserCog, Plus, Shield, Users, Trash2, Mail, Clock, Check, X, KeyRound } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { full_name: "", email: "", role: "user", weekly_capacity: 37.5, hourly_rate: 1250, skills: "", password: "" };

export default function UserManagement() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmClearDemo, setConfirmClearDemo] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");

  const isAdmin = user?.role === "admin";

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await apiClient.get("/admin/users")).data,
    enabled: isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => apiClient.post("/admin/users", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setShowCreate(false);
      setForm(emptyForm);
      toast.success("Bruker opprettet");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke opprette bruker"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/admin/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Bruker oppdatert");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/admin/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setDeleteTarget(null);
      toast.success("Bruker slettet");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette"),
  });

  const approveMutation = useMutation({
    mutationFn: (id) => apiClient.post(`/admin/users/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Bruker godkjent");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke godkjenne"),
  });

  const setPasswordMutation = useMutation({
    mutationFn: ({ id, password }) => apiClient.post(`/admin/users/${id}/password`, { password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setPwTarget(null);
      setPwValue("");
      toast.success("Passord oppdatert");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke sette passord"),
  });

  const clearDemoMutation = useMutation({
    mutationFn: () => apiClient.post("/admin/clear-demo-users"),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setConfirmClearDemo(false);
      const n = (res && res.data && res.data.users_deleted) || 0;
      toast.success(`${n} demo-medarbeidere slettet`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke slette demo-medarbeidere"),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate({
      full_name: form.full_name,
      email: form.email,
      role: form.role,
      password: form.password || undefined,
      weekly_capacity: Number(form.weekly_capacity) || 37.5,
      hourly_rate: Number(form.hourly_rate) || 1250,
      skills: form.skills ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-brand-ink">Krever administratortilgang</h2>
          <p className="text-slate-500 mt-2">Du har ikke tilgang til brukeradministrasjon.</p>
        </div>
      </div>
    );
  }

  const pendingUsers = users.filter((u) => u.status === "pending");
  const activeUsers = users.filter((u) => u.status !== "pending");
  const adminCount = activeUsers.filter((u) => u.role === "admin").length;
  const demoCount = users.filter((u) => u.is_demo && u.email !== user?.email).length;

  const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-ink text-brand-lime flex items-center justify-center shrink-0">
              <UserCog className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-brand-ink tracking-tight">Brukere</h1>
              <p className="text-slate-500 mt-0.5">Opprett og administrer brukere og roller</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {demoCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setConfirmClearDemo(true)}
                data-testid="clear-demo-users-button"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Slett alle demo-medarbeidere
              </Button>
            )}
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" /> Opprett bruker
            </Button>
          </div>
        </div>

        {pendingUsers.length > 0 && (
          <div className="rounded-2xl bg-white border border-amber-200 shadow-sm overflow-hidden mb-6" data-testid="pending-users-section">
            <div className="px-6 py-4 bg-amber-50 flex items-center gap-3 border-b border-amber-100">
              <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Clock className="w-5 h-5" /></div>
              <div>
                <p className="font-semibold text-brand-ink">Venter på godkjenning</p>
                <p className="text-sm text-slate-500">{pendingUsers.length} bruker(e) ønsker tilgang til systemet</p>
              </div>
              <span className="ml-auto text-lg font-extrabold text-amber-700" data-testid="pending-count">{pendingUsers.length}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {pendingUsers.map((u) => (
                <div key={u.user_id} data-testid={`pending-user-${u.email}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4">
                  <span className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">
                    {initials(u.full_name)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-brand-ink truncate">{u.full_name}</p>
                    <p className="text-sm text-slate-400 flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={approveMutation.isPending}
                      onClick={() => approveMutation.mutate(u.user_id)}
                      data-testid={`approve-user-${u.email}`}
                    >
                      <Check className="w-4 h-4 mr-1.5" /> Godkjenn
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setDeleteTarget(u)}
                      data-testid={`reject-user-${u.email}`}
                    >
                      <X className="w-4 h-4 mr-1.5" /> Avvis
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Totalt brukere</p>
              <p className="mt-2 text-2xl font-extrabold text-brand-ink">{activeUsers.length}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-brand-limeSoft text-brand-limeDark flex items-center justify-center"><Users className="w-5 h-5" /></div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Administratorer</p>
              <p className="mt-2 text-2xl font-extrabold text-brand-ink">{adminCount}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-brand-ink text-brand-lime flex items-center justify-center"><Shield className="w-5 h-5" /></div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <div className="col-span-5">Bruker</div>
            <div className="col-span-3">Rolle</div>
            <div className="col-span-2 text-right">Timepris</div>
            <div className="col-span-2 text-right">Handling</div>
          </div>
          {isLoading ? (
            <div className="p-10 text-center text-slate-400">Laster...</div>
          ) : activeUsers.length === 0 ? (
            <div className="p-10 text-center text-slate-400">Ingen brukere ennå</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {activeUsers.map((u) => (
                <div key={u.user_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 items-center hover:bg-slate-50/60 transition-colors">
                  <div className="col-span-5 flex items-center gap-3 min-w-0">
                    <span className="w-10 h-10 rounded-full bg-brand-ink text-brand-lime flex items-center justify-center text-xs font-bold shrink-0">
                      {initials(u.full_name)}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-brand-ink truncate">{u.full_name}</p>
                      <p className="text-sm text-slate-400 flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {u.email}</p>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <Select
                      value={u.role}
                      onValueChange={(val) => updateMutation.mutate({ id: u.user_id, data: { role: val } })}
                      disabled={u.user_id === user?.user_id}
                    >
                      <SelectTrigger className="w-36 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="user">Medarbeider</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 md:text-right">
                    <span className="text-sm text-slate-600">{(u.hourly_rate || 0).toLocaleString("nb-NO")} kr/t</span>
                    {u.role === "admin" && <Badge className="ml-2 md:hidden bg-brand-limeSoft text-brand-limeDark border-0">Admin</Badge>}
                  </div>
                  <div className="col-span-2 md:text-right flex items-center gap-1 md:justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-brand-ink hover:bg-slate-100"
                      title="Sett/endre passord"
                      onClick={() => { setPwTarget(u); setPwValue(""); }}
                      data-testid={`set-password-${u.email}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                      disabled={u.user_id === user?.user_id}
                      onClick={() => setDeleteTarget(u)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create user dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-brand-ink">Opprett ny bruker</DialogTitle>
            <DialogDescription>
              Brukeren logger inn med Google på samme e-post og får rollen du velger.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Navn</Label>
              <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ola Nordmann" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">E-post</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ola@murphy.no" className="mt-1.5" />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={form.role} onValueChange={(val) => setForm({ ...form, role: val })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Medarbeider</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="new-user-password">Passord (valgfritt)</Label>
              <Input id="new-user-password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="La stå tomt for kun Google-innlogging" className="mt-1.5" data-testid="create-user-password" />
              <p className="text-xs text-slate-400 mt-1">Settes et passord, kan brukeren logge inn med e-post + passord med én gang.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cap">Kapasitet (t/uke)</Label>
                <Input id="cap" type="number" step="0.5" value={form.weekly_capacity} onChange={(e) => setForm({ ...form, weekly_capacity: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="rate">Timepris (kr)</Label>
                <Input id="rate" type="number" step="50" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="skills">Kompetanse (kommaseparert)</Label>
              <Input id="skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Regnskap, Skatt" className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Avbryt</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Oppretter..." : "Opprett bruker"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set / change password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(v) => { if (!v) { setPwTarget(null); setPwValue(""); } }}>
        <DialogContent className="max-w-md" data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-ink">Sett / endre passord</DialogTitle>
            <DialogDescription>
              {pwTarget?.full_name} ({pwTarget?.email}) kan logge inn med e-post og dette passordet.
              {pwTarget?.has_password ? " Brukeren har allerede et passord — dette erstatter det." : ""}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); if (!pwValue) { toast.error("Skriv inn et passord"); return; } setPasswordMutation.mutate({ id: pwTarget.user_id, password: pwValue }); }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="pw-value">Nytt passord</Label>
              <Input id="pw-value" type="password" autoComplete="new-password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="set-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setPwTarget(null); setPwValue(""); }}>Avbryt</Button>
              <Button type="submit" disabled={setPasswordMutation.isPending} data-testid="set-password-save">
                {setPasswordMutation.isPending ? "Lagrer..." : "Lagre passord"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette bruker?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.full_name} ({deleteTarget?.email}) vil miste tilgang. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(deleteTarget.user_id)}>
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmClearDemo} onOpenChange={setConfirmClearDemo}>
        <AlertDialogContent data-testid="clear-demo-users-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette alle {demoCount} demo-medarbeidere?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle seedede demo-medarbeidere (Ola, Nora, Jonas m.fl.) fjernes permanent fra teamet
              og fra tildelingslistene. Din egen konto og brukere du selv har opprettet berøres{" "}
              <span className="font-semibold text-brand-ink">ikke</span>. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-clear-demo-users-button"
              className="bg-red-600 hover:bg-red-700"
              disabled={clearDemoMutation.isPending}
              onClick={() => clearDemoMutation.mutate()}
            >
              {clearDemoMutation.isPending ? "Sletter..." : `Slett ${demoCount} demo-medarbeidere`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
